import { Navigate, createBrowserRouter } from 'react-router';
import { AppShell } from './AppShell';
import { RequireAuth } from './RequireAuth';
import { RouteErrorBoundary } from './ErrorBoundary';
import { LoginPage } from '@/features/auth/LoginPage';
import { PlaceholderPage } from './PlaceholderPage';
import { TbmListPage } from '@/features/database/TbmListPage';
import { TbmDetailPage } from '@/features/database/TbmDetailPage';
import { PelitaListPage } from '@/features/database/PelitaListPage';
import { PelitaDetailPage } from '@/features/database/PelitaDetailPage';
import { PelitaIntensiveClassAdminPage } from '@/features/database/PelitaIntensiveClassAdminPage';
import { PelitaMyIntensiveClassPage } from '@/features/database/PelitaMyIntensiveClassPage';
import { TbmFormPage } from '@/features/database/TbmFormPage';
import { PelitaFormPage } from '@/features/database/PelitaFormPage';
import { ImportWizard } from '@/features/database/ImportWizard';
import { AssessmentListPage } from '@/features/assessments/AssessmentListPage';
import { AssessmentFormPage } from '@/features/assessments/AssessmentFormPage';
import { InstrumentListPage } from '@/features/instruments/InstrumentListPage';
import { InstrumentDetailPage } from '@/features/instruments/InstrumentDetailPage';
import { InstrumentImportPage } from '@/features/instruments/InstrumentImportPage';
import { FindingsPage } from '@/features/monev/FindingsPage';
import { ReflectionsPage } from '@/features/monev/ReflectionsPage';
import { PeriodsPage } from '@/features/settings/PeriodsPage';
import { RegionsPage } from '@/features/settings/RegionsPage';
import { UsersPage } from '@/features/settings/UsersPage';
import { DashboardPage } from '@/features/analytics/DashboardPage';
import { TbmReportPage } from '@/features/analytics/TbmReportPage';
import { IndividualReportPage } from '@/features/analytics/IndividualReportPage';
import { ComparisonPage } from '@/features/analytics/ComparisonPage';

/**
 * Halaman yang belum dibangun sengaja tetap punya rute + EmptyState, bukan
 * disembunyikan. Kerangka navigasi berdiri lebih dulu supaya tidak perlu
 * bongkar ulang saat Domain 1–4 menyusul.
 */
export const router = createBrowserRouter([
  { path: '/masuk', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    // Dipasang di induk, bukan di tiap rute: galat render satu halaman tidak
    // boleh membuat SELURUH aplikasi jadi layar putih, dan menuliskannya 25
    // kali hanya menunggu ada rute baru yang lupa dipasangi.
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },

      { path: 'database', element: <Navigate to="/database/tbm" replace /> },
      { path: 'database/tbm', element: <TbmListPage /> },
      { path: 'database/tbm/baru', element: <TbmFormPage /> },
      { path: 'database/tbm/impor', element: <ImportWizard kind="tbm" /> },
      { path: 'database/tbm/:id', element: <TbmDetailPage /> },
      { path: 'database/tbm/:id/ubah', element: <TbmFormPage /> },
      { path: 'database/pelita', element: <PelitaListPage /> },
      { path: 'database/pelita/baru', element: <PelitaFormPage /> },
      { path: 'database/kelas-intensif', element: <PelitaIntensiveClassAdminPage /> },
      { path: 'database/pelita/kelas-intensif', element: <Navigate to="/database/kelas-intensif" replace /> },
      { path: 'database/pelita/impor', element: <ImportWizard kind="individuals" /> },
      { path: 'database/pelita/:id', element: <PelitaDetailPage /> },
      { path: 'database/pelita/:id/ubah', element: <PelitaFormPage /> },
      { path: 'kelas-intensif', element: <PelitaMyIntensiveClassPage /> },

      { path: 'pengukuran', element: <Navigate to="/pengukuran/tbm" replace /> },
      { path: 'pengukuran/tbm', element: <AssessmentListPage scope="tbm" /> },
      { path: 'pengukuran/individu', element: <AssessmentListPage scope="individual" /> },
      // Capaian Program ditunda dari versi 1.0. Alamatnya dialihkan, bukan
      // dibiarkan kosong: tanpa ini ia tersangkut di rute `pengukuran/:id` di
      // bawah dan muncul sebagai "Penilaian tidak ditemukan" — galat yang
      // menyesatkan untuk tautan yang dulu sah.
      { path: 'pengukuran/capaian-program', element: <Navigate to="/pengukuran/tbm" replace /> },
      { path: 'pengukuran/:id', element: <AssessmentFormPage /> },

      { path: 'monev', element: <Navigate to="/monev/risalah" replace /> },
      { path: 'monev/risalah', element: <FindingsPage /> },
      { path: 'monev/refleksi', element: <ReflectionsPage /> },

      { path: 'analitik', element: <Navigate to="/analitik/tbm" replace /> },
      { path: 'analitik/tbm', element: <TbmReportPage /> },
      { path: 'analitik/individu', element: <IndividualReportPage /> },
      { path: 'analitik/perbandingan', element: <ComparisonPage /> },

      { path: 'pengaturan', element: <Navigate to="/pengaturan/instrumen" replace /> },
      { path: 'pengaturan/instrumen', element: <InstrumentListPage /> },
      { path: 'pengaturan/instrumen/:id', element: <InstrumentDetailPage /> },
      { path: 'pengaturan/instrumen/:id/impor', element: <InstrumentImportPage /> },
      { path: 'pengaturan/periode', element: <PeriodsPage /> },
      { path: 'pengaturan/wilayah', element: <RegionsPage /> },
      { path: 'pengaturan/pengguna', element: <UsersPage /> },

      { path: '*', element: <PlaceholderPage title="Halaman tidak ditemukan" fase="" /> },
    ],
  },
]);
